/* TurnWizard admin — the state every page shares.
 *
 * Three things live here and nothing else does: the tenant list and its filter,
 * which tenant is selected, and the handful of that tenant's tables that more
 * than one page reads. Everything narrower — the mailbox row, the members, the
 * ingest keys, the audit log — is loaded by the page that owns it, because a
 * store that holds a page's data is a store that has to know when that page is
 * open.
 *
 * The console's store.js is six thousand lines because it holds the rules of a
 * game. This one holds no rules at all: it moves rows, and what a setting means
 * is the console's business. Anything here that looks like a rule is a mistake
 * — it would be a second implementation of the board's behaviour, sitting where
 * nobody would think to update it.
 */
import * as Vue from 'vue';
import { api } from './api.js';
import { licensing } from './rules/licensing.js';
import { roles } from './rules/roles.js';
import { settingKeys } from './rules/settings-keys.js';
import { supportSession } from './rules/support-session.js';
import { tenantFilter } from './rules/tenant-filter.js';

/* ---------------- the tenant list ---------------- */

var tenants = Vue.ref([]);
var listLoaded = Vue.ref(false);

var filter = Vue.reactive({
  query: '',
  status: 'all',        // all | live | suspended
  connection: 'any',    // scheduling | no-scheduling | ingest | no-ingest
  activity: 'any',      // active | quiet
  license: 'any',       // licensed | expiring | lapsed | none
  sort: 'name'          // name | created | activity | turns
});

var visible = Vue.computed(function () {
  return tenantFilter.sortTenants(
    tenantFilter.filterTenants(tenants.value, filter, Date.now()), filter.sort);
});

var summary = Vue.computed(function () {
  return tenantFilter.summarise(visible.value.length, tenants.value.length);
});

var filtered = Vue.computed(function () {
  return filter.query !== '' || filter.status !== 'all' ||
    filter.connection !== 'any' || filter.activity !== 'any' ||
    filter.license !== 'any';
});

function clearFilter() {
  filter.query = '';
  filter.status = 'all';
  filter.connection = 'any';
  filter.activity = 'any';
  filter.license = 'any';
}

/* One round trip for the whole screen — see admin_tenant_overview() in
 * 12-admin-functions.sql for why this is a function rather than a dozen REST
 * calls per row.
 *
 * Two, now that there are licences, and 14-licensing.sql says why the second
 * one is a function of its own rather than more columns on the first: widening
 * that function means dropping it, and re-running part 12 would silently take
 * the licence columns away again and read every salon as unlicensed.
 *
 * They go up together and the licence half is allowed to fail. A project
 * without part 14 applied answers nothing to the second call, and every row
 * then carries `license_state: null` — which the list and the filter read as
 * "this build cannot tell", not as "unlicensed". Showing "No licence" beside
 * every salon on a database that has never heard of licensing would be the
 * worst of both answers.
 */
async function loadTenants() {
  var pair = await Promise.all([
    api.rpc('admin_tenant_overview'),
    api.rpc('admin_license_overview')
  ]);
  var rows = pair[0];
  if (!rows) return rows;

  var byTenant = {};
  (pair[1] || []).forEach(function (l) { byTenant[l.tenant_id] = l; });

  tenants.value = rows.map(function (t) {
    var l = byTenant[t.id];
    return Object.assign({}, t, {
      license_state: l ? l.state : null,
      license_plan_id: l ? l.plan_id : null,
      license_plan_name: l ? l.plan_name : null,
      license_expires_at: l ? l.expires_at : null,
      license_seats: l ? l.seats : null,
      license_grant_count: l ? l.grant_count : 0
    });
  });
  listLoaded.value = true;
  return rows;
}

/* ---------------- the selected tenant ---------------- */

var selectedId = Vue.ref(null);

/* Resolved out of the list rather than held as its own object, so a reload of
 * the list is also a reload of the header on every page — the counts, the
 * suspended flag and the name all follow without anything having to remember to
 * copy them across. A selection whose row has not arrived yet reads null, which
 * is the state the pages already have to handle on first load.
 */
var selected = Vue.computed(function () {
  if (!selectedId.value) return null;
  return tenants.value.find(function (t) { return t.id === selectedId.value; }) || null;
});

/* The tables more than one page needs.
 *
 * `settings` is the whole of tt_settings for the tenant, rows not values —
 * Branding, Board and Advanced all read it, and Advanced needs the rows
 * themselves so it can show a key this build does not understand.
 */
var data = Vue.reactive({
  settings: [],
  services: [],
  staff: [],
  loadedFor: null,
  loading: false
});

async function loadTenant(id, force) {
  if (!id) return;
  if (data.loadedFor === id && !force) return;
  data.loading = true;
  try {
    var settings = await api.rows('tt_settings', id);
    var services = await api.rows('tt_services', id, function (q) {
      return q.order('sort_order');
    });
    var staff = await api.rows('tt_staff', id, function (q) {
      return q.order('sort_order');
    });
    // A failed read leaves the previous tenant's rows on screen if it is
    // assigned blindly, which on this app means showing one salon's staff under
    // another's name. Nulls are refusals; keep what was there and let the error
    // in the top bar be the story.
    if (settings) data.settings = settings;
    if (services) data.services = services;
    if (staff) data.staff = staff;
    data.loadedFor = id;
  } finally {
    data.loading = false;
  }
}

// The licence goes up with the tenant's other shared tables rather than being
// left to the page that owns it, because three pages read it: Licence edits it,
// Overview leads with it, and People is bounded by the seat limit on it. That
// is the same test `settings` passes and the reason both are here.
function select(id) {
  selectedId.value = id;
  if (id) {
    loadTenant(id);
    loadLicense(id);
  }
}

/* ---------------- settings ---------------- */

function settingRow(key) {
  return data.settings.find(function (r) { return r.key === key; }) || null;
}

/* The value, or the console's fallback when there is no row.
 *
 * The distinction matters and is kept: `settingRow` says whether the salon has
 * ever set this, `settingValue` says what the board is doing about it. A page
 * that showed only the second could not tell an explicit `false` from a default
 * `false`, and clearing a key is one of the things this app is for.
 */
function settingValue(key) {
  var row = settingRow(key);
  if (row) return row.value;
  var m = settingKeys.meta(key);
  return m ? m.fallback : null;
}

function isSet(key) { return !!settingRow(key); }

/* Write one setting.
 *
 * `updated_at` is stamped by the client, matching what the console's prefs.js
 * sends — the column has a default, but a row written from here and a row
 * written from a station should carry the same kind of timestamp, and the
 * board's realtime subscription on tt_settings picks this change up either way.
 *
 * Returns false when the value is not one the key can hold, without writing.
 * The caller shows that as a refusal; nothing is stored and the control stays
 * where it was.
 */
async function setSetting(key, value) {
  var id = selectedId.value;
  if (!id) return false;

  var clean = settingKeys.clean(key, value);
  if (clean === undefined) {
    api.toast('That is not a value ' + key + ' can hold.', 'bad');
    return false;
  }

  var row = {
    key: key,
    value: clean === undefined ? null : clean,
    updated_at: new Date().toISOString()
  };
  var res = await api.writeRow('tt_settings', id, row, 'tenant_id,key');
  if (!res) return false;

  var existing = settingRow(key);
  if (existing) existing.value = row.value;
  else data.settings.push(Object.assign({ tenant_id: id }, row));

  api.log(id, 'setting.set', { key: key, value: row.value });
  return true;
}

/* Delete the row rather than write the default into it.
 *
 * This is an admin-only move and it is the reason the two are different
 * operations. The console's hydrate treats "no row" as "back to the default"
 * — see the note in js/prefs.js — so clearing a key genuinely resets a salon to
 * a fresh install's behaviour for that one setting, in a way that writing the
 * default value does not: a row saying `false` and no row at all diverge the
 * moment the default changes in a later build.
 */
async function clearSetting(key) {
  var id = selectedId.value;
  if (!id) return false;
  var res = await api.deleteRows('tt_settings', id, { key: key });
  if (!res) return false;
  data.settings = data.settings.filter(function (r) { return r.key !== key; });
  api.log(id, 'setting.clear', { key: key });
  api.toast('Cleared ' + key + ' — this salon is back to the default.');
  return true;
}

/* ---------------- the tenant row itself ---------------- */

async function saveTenant(patch) {
  var id = selectedId.value;
  if (!id) return false;
  var res = await api.run('Saving tenant', async function () {
    var r = await api.sb.from('tenants').update(patch).eq('id', id).select().single();
    if (r.error) throw new Error(r.error.message);
    return r.data;
  });
  if (!res) return false;
  var row = selected.value;
  if (row) Object.assign(row, patch);
  api.log(id, 'tenant.update', patch);
  return true;
}

// Suspension is severe enough to be its own call rather than a patch a form
// could send by accident — see the header of 10-platform-admins.sql for exactly
// what a suspended salon experiences.
async function setSuspended(on, reason) {
  var okay = await saveTenant({ suspended_at: on ? new Date().toISOString() : null });
  if (!okay) return false;
  api.log(selectedId.value, on ? 'tenant.suspend' : 'tenant.resume',
    { reason: reason || null });
  api.toast(on ? 'Suspended. The board reads and writes nothing until this is lifted.'
                : 'Resumed. Stations are back within a second or two.');
  return true;
}

/* ---------------- the clock ----------------
 *
 * A ticking value that anything counting down depends on. Without it a label is
 * computed once and then sits there reading "28m" for half an hour, which is
 * exactly the sort of stale reassurance the two numbers in this app must not
 * give: how long a support session has left, and how long a licence has.
 *
 * Fifteen seconds is fine for both. The session's smallest unit is a minute and
 * the last five are what it is for; the licence's is a day, and what the tick
 * actually buys there is the moment it runs out — a screen left open across the
 * expiry should stop saying "active" on its own.
 */
var clock = Vue.ref(Date.now());
setInterval(function () { clock.value = Date.now(); }, 15000);

/* ---------------- licensing ----------------
 *
 * The catalogue is shared — the Plans screen edits it and every salon's Licence
 * page reads it — so it lives here. A salon's grants do not: they are one
 * page's data, and a store that holds a page's rows is a store that has to know
 * when that page is open.
 *
 * The exception is the reading the LIST needs, which comes down with the tenant
 * rows in `loadTenants` above rather than from here. Loading eleven salons'
 * grant histories to draw eleven pills would be the wrong shape entirely.
 */
var plans = Vue.ref([]);
var plansLoaded = Vue.ref(false);

async function loadPlans(force) {
  if (plansLoaded.value && !force) return plans.value;
  var rows = await api.query('Loading plans', function (sb) {
    return sb.from('license_plans').select('*').order('sort_order').order('name');
  });
  if (rows) {
    plans.value = rows;
    plansLoaded.value = true;
  }
  return rows;
}

// The plans a NEW grant may be made on. A retired plan keeps working for every
// salon already holding it — the grants point at it and the history has to keep
// reading correctly — it is simply not offered again.
var offeredPlans = Vue.computed(function () {
  return plans.value.filter(function (p) { return !p.retired; });
});

var defaultPlan = Vue.computed(function () {
  return plans.value.find(function (p) { return p.is_default; }) || null;
});

/* One salon's grants, newest first. Held here rather than in the page only
 * because the People page wants the seat limit too, and asking two pages to
 * load the same history separately is how they end up disagreeing about it.
 */
var license = Vue.reactive({
  rows: [],
  loadedFor: null,
  loading: false
});

async function loadLicense(id, force) {
  if (!id) return;
  if (license.loadedFor === id && !force) return;
  license.loading = true;
  try {
    await loadPlans();
    var rows = await api.rows('tenant_licenses', id, function (q) {
      return q.order('starts_at', { ascending: false }).order('created_at', { ascending: false });
    });
    // A refusal leaves the previous salon's grants on screen if it is assigned
    // blindly, which on this app means showing one business's licence under
    // another's name. Same rule as loadTenant above.
    if (rows) {
      license.rows = rows;
      license.loadedFor = id;
    }
  } finally {
    license.loading = false;
  }
}

/* What the selected salon is entitled to, worked out from the rows on screen.
 *
 * The database has the same reading — `private.license_resolve()`, which is
 * what the salon's own console asks and is therefore the authority. This one
 * exists because the page is holding the whole history anyway and needs to
 * label every row in it. See the header of js/rules/licensing.js for why two
 * implementations of one rule is a cost worth paying here and nowhere else.
 */
var entitlement = Vue.computed(function () {
  if (license.loadedFor !== selectedId.value) {
    return licensing.entitlement([], plans.value, Date.now());
  }
  return licensing.entitlement(license.rows, plans.value, clock.value);
});

var seats = Vue.computed(function () {
  var t = selected.value;
  return licensing.seatState(entitlement.value.seats, t ? t.member_count : 0);
});

/* Grant a licence.
 *
 * An INSERT, always — never an update of the row that is there. A grant is what
 * happened, and renewing, moving somebody up a plan and correcting yesterday's
 * mistake are three different things that all leave the previous grant legible.
 * The column grants in 14-licensing.sql make that structural rather than a
 * convention this file is keeping: `granted_by` is not insertable from a
 * browser and only `revoked_at` and `note` are updatable.
 */
async function grantLicense(fields) {
  var id = selectedId.value;
  if (!id) return false;

  var seatCount = licensing.cleanSeats(fields.seats);
  if (seatCount === undefined) {
    api.toast('A licence needs at least one seat, or none at all for no limit.', 'bad');
    return false;
  }

  var row = {
    plan_id: fields.planId,
    features: licensing.normalise(fields.features),
    seats: seatCount,
    starts_at: fields.startsAt || new Date().toISOString(),
    expires_at: fields.expiresAt || null,
    note: (fields.note || '').trim() || null
  };

  var res = await api.run('Granting licence', async function () {
    var r = await api.sb.from('tenant_licenses')
      .insert(Object.assign({ tenant_id: id }, row))
      .select().single();
    if (r.error) throw new Error(r.error.message);
    return r.data;
  });
  if (!res) return false;

  api.log(id, 'license.grant', {
    plan: row.plan_id, seats: row.seats, features: row.features,
    starts_at: row.starts_at, expires_at: row.expires_at
  });
  await loadLicense(id, true);
  loadTenants();
  return true;
}

/* Withdraw one.
 *
 * Not a delete — there is no delete grant on the table at all. A licence that
 * was withdrawn and a licence that never existed are different facts, and only
 * one of them has to be explained to somebody later.
 *
 * Revoking the grant in force does NOT leave the salon on the one before it: a
 * superseded grant stays superseded, so what this does is take the salon to
 * whichever earlier grant is still standing, or to nothing. The page says so
 * before it happens, because "revoke" reads like "undo" and is not.
 */
async function revokeGrant(row, reason) {
  var id = selectedId.value;
  if (!id || !row) return false;
  var res = await api.updateRow('tenant_licenses', id, { id: row.id }, {
    revoked_at: new Date().toISOString(),
    note: (reason || '').trim() || row.note || null
  });
  if (!res) return false;
  api.log(id, 'license.revoke', { plan: row.plan_id, reason: reason || null });
  await loadLicense(id, true);
  loadTenants();
  return true;
}

/* ---------------- the plan catalogue ----------------
 *
 * Editing a plan changes what EVERY salon on it is entitled to, at once and
 * without anybody granting anything. That is the point of a plan and it is also
 * the hazard, so these three are deliberately plain calls that the Plans screen
 * wraps in a warning rather than helpers that make it feel routine.
 */
/* One salon's roles (15-roles.sql). Held here for the same reason the licence
 * is: the Roles page edits them and the People page offers them in its menus,
 * and two copies would disagree the moment a role was renamed on one. */
var roleState = Vue.reactive({
  rows: [],
  loadedFor: null,
  // The schema is not applied on this project: the table is missing. People
  // falls back to owner/station rather than offering an empty menu.
  missing: false
});

async function loadRoles(id, force) {
  if (!id) return;
  if (roleState.loadedFor === id && !force) return;
  var res = await api.run('Loading roles', async function () {
    var r = await api.sb.from('tenant_roles').select('*').eq('tenant_id', id);
    // PostgREST's "relation does not exist" — not an error worth a toast.
    if (r.error && (r.error.code === '42P01' || r.error.code === 'PGRST205')) return { missing: true };
    if (r.error) throw new Error(r.error.message);
    return { rows: r.data || [] };
  });
  if (!res) return;
  roleState.missing = !!res.missing;
  roleState.rows = roles.sorted(res.rows || []);
  roleState.loadedFor = id;
}

async function createRole(row) {
  var id = selectedId.value;
  var res = await api.run('Creating role', async function () {
    var r = await api.sb.from('tenant_roles')
      .insert(Object.assign({}, row, { tenant_id: id })).select().single();
    if (r.error) throw new Error(r.error.message);
    return r.data;
  });
  if (!res) return false;
  api.log(id, 'role.create', { role: row.id, name: row.name, features: row.features });
  await loadRoles(id, true);
  return true;
}

async function saveRole(roleId, patch) {
  var id = selectedId.value;
  var res = await api.updateRow('tenant_roles', id, { id: roleId }, patch);
  if (!res) return false;
  api.log(id, 'role.update', Object.assign({ role: roleId }, patch));
  await loadRoles(id, true);
  return true;
}

async function deleteRole(roleId) {
  var id = selectedId.value;
  var res = await api.deleteRows('tenant_roles', id, { id: roleId });
  if (!res) return false;
  api.log(id, 'role.delete', { role: roleId });
  await loadRoles(id, true);
  return true;
}

async function savePlan(id, patch) {
  var res = await api.run('Saving plan', async function () {
    var r = await api.sb.from('license_plans')
      .update(Object.assign({}, patch, { updated_at: new Date().toISOString() }))
      .eq('id', id).select().single();
    if (r.error) throw new Error(r.error.message);
    return r.data;
  });
  if (!res) return false;
  api.log(null, 'plan.update', Object.assign({ plan: id }, patch));
  await loadPlans(true);
  return true;
}

async function createPlan(row) {
  var res = await api.run('Creating plan', async function () {
    var r = await api.sb.from('license_plans').insert(row).select().single();
    if (r.error) throw new Error(r.error.message);
    return r.data;
  });
  if (!res) return false;
  api.log(null, 'plan.create', { plan: row.id, features: row.features });
  await loadPlans(true);
  return true;
}

/* One default, enforced by a unique index over the true rows — so moving it
 * means clearing the old one before setting the new one, and as two calls from
 * here that is a window in which NO plan is the default. A tenant created
 * inside that window is created unlicensed, which is exactly the state a
 * default exists to prevent, so the pair is one statement in the database.
 * `admin_set_default_plan` writes its own audit row.
 */
async function setDefaultPlan(id) {
  var res = await api.rpc('admin_set_default_plan', { p_plan: id });
  if (!res) return false;
  await loadPlans(true);
  return true;
}

/* ---------------- support sessions ----------------
 *
 * "Log in as this salon". The row lives in `admin_impersonation` and is read by
 * the tenant lookup every policy in the product runs, so while one is live this
 * admin's CONSOLE session is that salon — see
 * admin/server/schema/13-impersonation.sql.
 *
 * Nothing about this app changes while it is on. The admin app reaches every
 * salon through `is_platform_admin()` and never through the tenant lookup, so a
 * support session cannot move what is on screen here. That is worth stating
 * plainly because the opposite would be the obvious guess: starting one does not
 * put this app "into" a salon, it puts the CONSOLE into one.
 */
var support = Vue.reactive({
  tenantId: null,
  tenantName: null,
  startedAt: null,
  expiresAt: null,
  reason: null,
  loaded: false
});

var supportLeft = Vue.computed(function () {
  return supportSession.remaining(support.expiresAt, clock.value);
});

// The session as the database would see it: a row that has run out is not a
// session, because every policy has already stopped honouring it.
var supporting = Vue.computed(function () {
  return support.tenantId && !supportLeft.value.expired ? support : null;
});

var supportingSelected = Vue.computed(function () {
  return !!supporting.value && support.tenantId === selectedId.value;
});

function setSupport(row) {
  support.tenantId = row ? row.tenant_id : null;
  support.tenantName = row ? row.tenant_name : null;
  support.startedAt = row ? row.started_at : null;
  support.expiresAt = row ? row.expires_at : null;
  support.reason = row ? row.reason : null;
  support.loaded = true;
}

async function loadSupport() {
  var rows = await api.rpc('admin_impersonation_status');
  // A refusal (null) is not "no session" — leave what is on screen rather than
  // telling somebody their console is their own when the request simply failed.
  if (rows == null) return;
  setSupport(rows[0] || null);
}

async function startSupport(tenantId, minutes, reason) {
  var expires = await api.rpc('admin_impersonate', {
    p_tenant: tenantId,
    p_minutes: supportSession.clampMinutes(minutes),
    p_reason: reason || null
  });
  if (!expires) return false;
  await loadSupport();
  return true;
}

async function stopSupport() {
  var res = await api.rpc('admin_stop_impersonating');
  if (res == null) return false;
  setSupport(null);
  return true;
}

// Every live session, across every admin — the Admins screen reads this. Not
// the same query as the one above: that one asks about the caller, this asks
// about everybody, which only an admin's read policy allows.
async function loadAllSupport() {
  return api.query('Loading support sessions', function (sb) {
    return sb.from('admin_impersonation')
      .select('*').gt('expires_at', new Date().toISOString()).order('expires_at');
  });
}

/* ---------------- formatting ----------------
 *
 * Shared because every page shows a timestamp and they should all read the
 * same. Deliberately plain: this app is used by two people who know what a
 * timestamp is, so the hover title is the exact value and the label is the
 * rough one.
 */
function ago(value) {
  if (!value) return 'never';
  var ms = Date.now() - new Date(value).getTime();
  if (ms < 0) return 'in the future';
  var mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  var hours = Math.round(mins / 60);
  if (hours < 48) return hours + 'h ago';
  return Math.round(hours / 24) + 'd ago';
}

function stamp(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString([], {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

function today() {
  var d = new Date();
  return [d.getFullYear(),
          String(d.getMonth() + 1).padStart(2, '0'),
          String(d.getDate()).padStart(2, '0')].join('-');
}

export const store = {
  tenants: tenants,
  listLoaded: listLoaded,
  filter: filter,
  filtered: filtered,
  clearFilter: clearFilter,
  visible: visible,
  summary: summary,
  loadTenants: loadTenants,

  selectedId: selectedId,
  selected: selected,
  select: select,
  loadTenant: loadTenant,
  data: data,

  settingRow: settingRow,
  settingValue: settingValue,
  isSet: isSet,
  setSetting: setSetting,
  clearSetting: clearSetting,

  saveTenant: saveTenant,
  setSuspended: setSuspended,

  plans: plans,
  plansLoaded: plansLoaded,
  offeredPlans: offeredPlans,
  defaultPlan: defaultPlan,
  loadPlans: loadPlans,
  savePlan: savePlan,
  createPlan: createPlan,
  setDefaultPlan: setDefaultPlan,

  license: license,
  loadLicense: loadLicense,
  entitlement: entitlement,
  seats: seats,
  grantLicense: grantLicense,
  revokeGrant: revokeGrant,

  roles: roleState,
  loadRoles: loadRoles,
  createRole: createRole,
  saveRole: saveRole,
  deleteRole: deleteRole,

  support: support,
  supporting: supporting,
  supportingSelected: supportingSelected,
  supportLeft: supportLeft,
  supportChoices: supportSession.CHOICES,
  loadSupport: loadSupport,
  startSupport: startSupport,
  stopSupport: stopSupport,
  loadAllSupport: loadAllSupport,

  ago: ago,
  stamp: stamp,
  today: today
};
